import React, { useEffect, useState } from "react";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  region?: string;
  photo: {
    url: string;
  };
  isFeatured?: boolean;
}

const PeopleBehindMission: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Placeholder API call to fetch team members
    const fetchTeamMembers = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/api/team-members?populate=*&sort=order:asc`
        );

        if (response.ok) {
          const data = await response.json();
          setTeamMembers(data.data || []);
        } else {
          // Fallback data
          setTeamMembers([
            {
              id: 1,
              name: "Jasper & Jane Gitonga",
              role: "International Directors & Founders",
              photo: { url: "" },
              isFeatured: true,
            },
            {
              id: 2,
              name: "Wycliffe & Rebecca",
              role: "Nairobi Region, Kenya",
              photo: { url: "" },
            },
            {
              id: 3,
              name: "Rich & Mercy Gichuki",
              role: "Coast Region, Kenya",
              photo: { url: "" },
            },
            {
              id: 4,
              name: "Annie & Stanley Munene",
              role: "Western Region, Kenya",
              photo: { url: "" },
            },
            {
              id: 5,
              name: "Johnny & Bell",
              role: "Country coordinators, Rwanda",
              photo: { url: "" },
            },
            {
              id: 6,
              name: "Mia Ward",
              role: "Country coordinator, Uganda",
              photo: { url: "" },
            },
            {
              id: 7,
              name: "Archie Young",
              role: "Country Coordinator, South Sudan",
              photo: { url: "" },
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
        // Fallback data on error
        setTeamMembers([
          {
            id: 1,
            name: "Jasper & Jane Gitonga",
            role: "International Directors & Founders",
            photo: { url: "" },
            isFeatured: true,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  const featuredMember = teamMembers.find((member) => member.isFeatured);
  const regularMembers = teamMembers.filter((member) => !member.isFeatured);

  if (isLoading) {
    return (
      <div className="container mx-auto py-16 px-4">
        <div className="text-center">Loading team members...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-16 px-4">
      <div className="mb-12 flex flex-col items-center">
        <p className="text-navYellow text-xl font-medium mb-2">Meet the Team</p>
        <h2 className="text-3xl md:text-4xl font-medium text-darkBlue">
          The People Behind the Mission
        </h2>
      </div>

      {featuredMember && (
        <div className="mb-10 flex justify-center">
          <div className="relative lg:w-3/5 rounded-2xl overflow-hidden group cursor-pointer aspect-[5/3]">
            {featuredMember.photo.url ? (
              <img
                src={featuredMember.photo.url}
                alt={featuredMember.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-lightBlue to-purpleBlue flex items-center justify-center">
                <div className="text-white text-center">
                  <svg
                    className="w-24 h-24 mx-auto mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <p>Photo placeholder</p>
                </div>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
              <h3 className="text-white text-xl md:text-3xl font-bold mb-1">
                {featuredMember.name}
              </h3>
              <p className="text-gray-300 text-base">{featuredMember.role}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Regular team members */}
        {regularMembers.map((member) => (
          <div
            key={member.id}
            className="relative rounded-2xl overflow-hidden group cursor-pointer"
          >
            <div className="aspect-[4/5] w-full">
              {member.photo.url ? (
                <img
                  src={member.photo.url}
                  alt={member.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-lightBlue to-purpleBlue flex items-center justify-center">
                  <div className="text-white text-center">
                    <svg
                      className="w-16 h-16 mx-auto mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    <p className="text-xs">Photo placeholder</p>
                  </div>
                </div>
              )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
              <h3 className="text-white font-bold mb-1">{member.name}</h3>
              <p className="text-gray-300 text-xs">{member.role}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PeopleBehindMission;

